import { PaginatedQueryResult } from '@query/usePaginatedQuery'
import { TFunction } from 'i18next'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useModal } from '@context/ModalsContext'
import { useSnackbar } from '@context/SnackbarContext'

import { Button, IconButton } from '@ui/components/Button'
import { PageSection } from '@ui/components/PageSection'
import { CircleCheckIcon } from '@ui/icons/CircleCheck.icon'
import { CircleCloseIcon } from '@ui/icons/CircleClose.icon'
import { PercentIcon } from '@ui/icons/Percent.icon'
import { Pagination } from '@ui/v2/Pagination/Pagination'
import { TableBulkActions } from '@ui/v2/Table/Table'
import { FilterOptions, TableFilter } from '@ui/v2/TableFilter/TableFilter'
import { TableSummary } from '@ui/v2/TableSummary/TableSummary'

import { ResponseError, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useWindowResize } from '@utils/domUtils'

import { NameDto } from '@model/NameDto'
import {
  IBRebateApprovalSortKeysDto,
  IntroducingBrokerRebateApprovalDto,
  RebateApprovalStatus,
  rebateApprovalEditableStatuses,
} from '@model/RebateApprovalsDto'

import { ScrollToIds } from '@hooks/useScrollToElementIds'

import { ConfirmModal } from '@shared/modals/Confirm.modal'
import { FromToModal } from '@shared/modals/table/FromTo.modal'

import { useIBRebatesPageOutletContext } from '../RebatesPage'
import { ApprovalsPageTable } from './ApprovalsPageTable'
import { useIBApprovalsFilterOptions } from './hooks/useIBApprovalsFilterOptions'
import { useIBRebateApprovals } from './hooks/useIBRebateApprovals'
import { useIBRebateApprovalsSummary } from './hooks/useIBRebateApprovalsSummary'

interface IIBRebateApprovalsPage {
  isRebatesManagement?: boolean
}

export const IBRebateApprovalsPage = ({ isRebatesManagement }: IIBRebateApprovalsPage) => {
  const { t } = useTranslation()
  const { account } = useAccountReadContext()
  const isMobile = useWindowResize()
  const { addSnackbar } = useSnackbar()
  const [isFilterVisible, setIsFilterVisible] = useState(false)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const { refreshIBNotifications } = useIBRebatesPageOutletContext()
  const { groups, tradingAccounts, accountTypes } = useIBApprovalsFilterOptions()
  const confirmActionModal = useModal(ConfirmModal, { title: t('Please, confirm your action') })
  const recalculateModal = useModal(FromToModal<number>, {
    title: t('IB.RebatesPage.ApprovalsPage.Recalculate rebate'),
    inputIcon: PercentIcon,
    description: t('IB.RebatesPage.ApprovalsPage.New changes will be applied to current'),
  })

  const {
    query: tableQuery,
    sortOptions: tableSortOptions,
    search: tableSearch,
    setSearch: setTableSearch,
    forceRefresh: forceRefreshTable,
  } = useIBRebateApprovals({
    onCompleted: () => refreshIBNotifications(),
    overrides: isRebatesManagement
      ? {
          pageSize: 5,
        }
      : undefined,
  })

  const clientApi = useApiClient(ClientApiClient)

  const {
    summaryItems,
    forceRefresh: forceRefreshApprovalsSummary,
    isLoading: isSummaryLoading,
  } = useIBRebateApprovalsSummary(tableSearch)

  const sortByStatusPriority = () => {
    tableQuery.setPageQuery?.({
      sort: 'statusPriority',
      sortOrder: 'ASC',
    })
  }

  const RebateApprovalStatusOptions: NameDto<RebateApprovalStatus>[] = [
    {
      id: RebateApprovalStatus.New,
      name: t('New'),
    },
    {
      id: RebateApprovalStatus.Pending,
      name: t('Pending'),
    },
    {
      id: RebateApprovalStatus.Approved,
      name: t('Approved'),
    },
    {
      id: RebateApprovalStatus.Rejected,
      name: t('Rejected'),
    },
    {
      id: RebateApprovalStatus.Failed,
      name: t('Failed'),
    },
    {
      id: RebateApprovalStatus.Reversed,
      name: t('Reversed'),
    },
  ]

  const handleApprove = async (ids: string[] | string) => {
    try {
      const ibId = account?.clientIntroducingBroker?.id ?? ''
      const isSingleId = typeof ids === 'string' || (Array.isArray(ids) && ids.length === 1)

      if (isSingleId) {
        const id = Array.isArray(ids) ? ids[0] : ids
        const response = await clientApi.approveSingleRebate(ibId, id)

        if (
          response.status.id === RebateApprovalStatus.Failed &&
          response.maxAllowedPercentage >= 0
        ) {
          addSnackbar.info({
            message: t('IB.RebatesPage.ApprovalsPage.walletInsufficientBalanceRecalculate', {
              percentage: response.maxAllowedPercentage,
            }),
          })
        } else {
          addSnackbar.success({
            message: t('IB.RebatesPage.ApprovalsPage.You have successfully approved_one', {
              count: 1,
            }),
          })
        }
      } else {
        const { skipped, processed } = await clientApi.approveBulkRebates(ibId, ids)
        if (processed.length) {
          addSnackbar.success({
            message: t('IB.RebatesPage.ApprovalsPage.You have successfully approved_other', {
              count: processed.length,
            }),
          })
        }
        if (skipped.length) {
          addSnackbar.warning({
            message: t(
              skipped.length === 1
                ? 'IB.RebatesPage.ApprovalsPage.Rebate Skipped_one'
                : 'IB.RebatesPage.ApprovalsPage.Rebate Skipped_other',
              {
                count: skipped.length,
              }
            ),
          })
        }
      }

      if (!tableQuery.pageQuery?.sort) {
        sortByStatusPriority()
      }

      refreshData()
    } catch (error: unknown) {
      if (error instanceof ResponseError) {
        if (error.response.response?.data.code === 'rebate_payout_status_can_not_be_changed') {
          addSnackbar.error({
            message: t('IB.RebatesPage.ApprovalsPage.Cannot update finalized rebates'),
          })
        } else if (error.response.response?.data.code === 'insufficient_wallet_balance') {
          addSnackbar.error({
            message: t('errors.Wallet has insufficient balance'),
          })
          refreshData()
        }
      }
    }
  }

  const handleReject = async (ids: string[] | string) => {
    try {
      const ibId = account?.clientIntroducingBroker?.id ?? ''

      if (typeof ids === 'string') {
        await clientApi.rejectRebate(ibId, ids)
        addSnackbar.success({
          message: t('IB.RebatesPage.ApprovalsPage.You have successfully rejected_one', {
            count: 1,
          }),
        })
      } else {
        const { skipped, processed } = await clientApi.rejectBulkRebates(ibId, ids)
        if (processed.length) {
          addSnackbar.success({
            message: t('IB.RebatesPage.ApprovalsPage.You have successfully rejected_other', {
              count: processed.length,
            }),
          })
        }
        if (skipped.length) {
          addSnackbar.warning({
            message: t(
              skipped.length === 1
                ? 'IB.RebatesPage.ApprovalsPage.Rebate Skipped_one'
                : 'IB.RebatesPage.ApprovalsPage.Rebate Skipped_other',
              {
                count: skipped.length,
              }
            ),
          })
        }
      }

      if (!tableQuery.pageQuery?.sort) {
        sortByStatusPriority()
      }

      refreshData()
    } catch (error: unknown) {
      if (
        error instanceof ResponseError &&
        error.response.response?.data.code === 'rebate_payout_status_can_not_be_changed'
      ) {
        addSnackbar.error({
          message: t('IB.RebatesPage.ApprovalsPage.Cannot update finalized rebates'),
        })
      }
    }
  }

  const handleRecalculate = async (approvalId: string, percentage: number) => {
    if (!percentage || isNaN(percentage) || percentage < 0.1 || percentage > 100) {
      addSnackbar.error({
        message: t(
          'IB.RebatesPage.ApprovalsPage.Please enter a valid percentage between 0 and 100'
        ),
      })
      return
    }

    try {
      if (!account?.clientIntroducingBroker?.id) {
        return
      }
      await clientApi.recalculateRebate(account?.clientIntroducingBroker.id, approvalId, percentage)
      addSnackbar.success({
        message: t('IB.RebatesPage.ApprovalsPage.Successfully recalculated rebate'),
      })
      refreshData()
    } catch (error: unknown) {
      if (
        error instanceof ResponseError &&
        error.response.response?.data.code === 'rebate_payout_status_can_not_be_changed'
      ) {
        addSnackbar.error({
          message: t('IB.RebatesPage.ApprovalsPage.Cannot update finalized rebates'),
        })
      }
    }
  }

  const handlePageChange = (pageIndex: number, pageSize: number) => {
    tableQuery.setPageQuery?.({
      ...tableQuery.pageQuery,
      pageIndex,
      pageSize,
    })
  }

  const isAnyFilterActive = useMemo(
    () => Object.values(tableSearch || {}).some((value) => !!value),
    [tableSearch]
  )

  const refreshData = () => {
    setSelectedIds([])
    forceRefreshApprovalsSummary()
    forceRefreshTable()
  }

  const handleCheckBoxClick = () => {
    if (isBulkEditMode) {
      setSelectedIds([])

      return
    }

    setSelectedIds(
      tableQuery.data
        .filter((item) => rebateApprovalEditableStatuses.includes(item.status.id))
        .map((item) => item.id)
    )
  }

  const handleConfirmApprove = async (groupId?: string) => {
    await handleApprove(groupId ?? selectedIds)
    confirmActionModal.close()
  }
  const handleConfirmReject = async (groupId?: string) => {
    await handleReject(groupId ?? selectedIds)
    confirmActionModal.close()
  }

  const getDescription = (selectedIdsCount?: number) => {
    const count = selectedIdsCount ?? selectedIds.length
    return {
      reject: t('IB.RebatesPage.ApprovalsPage.You are about to Reject', {
        count,
      }),
      approve: t('IB.RebatesPage.ApprovalsPage.You are about to Approve', {
        count,
      }),
    }
  }

  const getRebatePercentage = (rebate: IntroducingBrokerRebateApprovalDto) =>
    Math.round((rebate.rebateAmount / rebate.accumulatedCommission) * 100)
  const isBulkEditMode = selectedIds.length > 0

  return (
    <div>
      {!isRebatesManagement && (
        <TableSummary isLoading={isSummaryLoading} summaryItems={summaryItems} />
      )}

      {!isRebatesManagement && (
        <PageSection
          title={t('IB.RebatesPage.Trading accounts')}
          id={ScrollToIds.IBRebateApprovalsHeader}
          onFilterClick={() => setIsFilterVisible((prevState) => !prevState)}
          isFilterButtonDisabled={isBulkEditMode}
        />
      )}

      {!isBulkEditMode && (
        <TableFilter
          isVisible={isRebatesManagement || isFilterVisible}
          searchFilters={tableSearch}
          filterOptions={
            isRebatesManagement
              ? [
                  FilterOptions.AccumulatedCommission,
                  FilterOptions.RebateAmount,
                  FilterOptions.Status,
                ]
              : [
                  FilterOptions.TradingAccountId,
                  FilterOptions.TradingAccountType,
                  FilterOptions.IBRebateGroupId,
                  FilterOptions.AccumulatedVolumeFxm,
                  FilterOptions.AccumulatedVolumeOther,
                  FilterOptions.AccumulatedCommission,
                  FilterOptions.RebateAmount,
                ]
          }
          statusOptions={RebateApprovalStatusOptions}
          customLabels={{
            [FilterOptions.IBRebateGroupId]: t('IB.RebatesPage.ClientsPage.Group'),
          }}
          groupSearchProps={{
            options: groups || [],
          }}
          tradingAccountTypeOptions={accountTypes || []}
          tradingAccountSearchProps={{
            options: tradingAccounts || [],
          }}
          setSearchFilters={(searchFilters) => {
            if (!searchFilters) {
              return
            }
            setTableSearch(searchFilters)
            tableQuery.setPageQuery?.({
              ...tableQuery.pageQuery,
              pageIndex: 1,
            })
          }}
          size={isRebatesManagement ? 'sm' : 'md'}
        />
      )}

      <ApprovalsPageTable
        tableQuery={isRebatesManagement ? filterTableQuery(tableQuery) : tableQuery}
        sortOptions={
          isRebatesManagement ? filterSortOptions(tableSortOptions, t) : tableSortOptions
        }
        sticky='left'
        isAnyFilterActive={isAnyFilterActive}
        onSelect={setSelectedIds}
        selectedIds={selectedIds}
        onRecalculate={(group) =>
          recalculateModal.open({
            from: {
              value: getRebatePercentage(group),
            },
            onConfirm: (from, to) => handleRecalculate(group.id, to),
          })
        }
        onApprove={(group) =>
          confirmActionModal.open({
            onConfirm: () => handleConfirmApprove(group.id),
            description: getDescription(1).approve,
          })
        }
        onReject={(group) =>
          confirmActionModal.open({
            onConfirm: () => handleConfirmReject(group.id),
            description: getDescription(1).reject,
          })
        }
        isRebatesManagement={isRebatesManagement}
        topContent={
          isBulkEditMode && (
            <TableBulkActions
              selectedCount={selectedIds.length}
              isCheckboxActive={isBulkEditMode}
              isCheckboxIndeterminate={
                isBulkEditMode && selectedIds.length < tableQuery.data.length
              }
              onCheckboxClick={handleCheckBoxClick}
            >
              {!isMobile ? (
                <>
                  <Button.Tertiary
                    size='sm'
                    disabled={!selectedIds.length}
                    iconLeft={CircleCloseIcon}
                    onClick={() =>
                      confirmActionModal.open({
                        onConfirm: handleConfirmReject,
                        description: getDescription().reject,
                      })
                    }
                  >
                    {t('IB.RebatesPage.ApprovalsPage.Reject')}
                  </Button.Tertiary>

                  <Button.Tertiary
                    size='sm'
                    disabled={!selectedIds.length}
                    iconLeft={CircleCheckIcon}
                    onClick={() =>
                      confirmActionModal.open({
                        onConfirm: handleConfirmApprove,
                        description: getDescription().approve,
                      })
                    }
                  >
                    {t('IB.RebatesPage.ApprovalsPage.Approve')}
                  </Button.Tertiary>
                </>
              ) : (
                <>
                  <IconButton.Tertiary
                    size='sm'
                    icon={CircleCloseIcon}
                    disabled={!selectedIds.length}
                    onClick={() =>
                      confirmActionModal.open({
                        onConfirm: handleConfirmReject,
                        description: getDescription().reject,
                      })
                    }
                  />

                  <IconButton.Tertiary
                    size='sm'
                    icon={CircleCheckIcon}
                    disabled={!selectedIds.length}
                    onClick={() =>
                      confirmActionModal.open({
                        onConfirm: handleConfirmApprove,
                        description: getDescription().approve,
                      })
                    }
                  />
                </>
              )}

              <Button.Tertiary size='sm' onClick={() => setSelectedIds([])}>
                {t('IB.RebatesPage.ClientsPage.Cancel')}
              </Button.Tertiary>
            </TableBulkActions>
          )
        }
      />

      {tableQuery.meta && tableQuery.hasInitialResults && (
        <Pagination
          hidePageSize={isRebatesManagement}
          scrollToHeaderId={ScrollToIds.IBRebateApprovalsHeader}
          maxPageSize={100}
          pageData={tableQuery.meta}
          isLoading={tableQuery.isLoading}
          onPageChanged={handlePageChange}
        />
      )}
    </div>
  )
}

const filterTableQuery = (tableQuery: PaginatedQueryResult<IntroducingBrokerRebateApprovalDto>) => {
  const TABLE_QUERY_DATA_COLUMNS = [
    'tradingAccount',
    'accumulatedCommission',
    'rebateAmount',
    'status',
    'id',
  ]

  const filteredData = tableQuery.data.map((dataItem) => {
    return Object.fromEntries(
      Object.entries(dataItem).filter(([key]) => TABLE_QUERY_DATA_COLUMNS.includes(key))
    )
  })

  return { ...tableQuery, data: filteredData }
}

const filterSortOptions = (
  sortOptions: NameDto<IBRebateApprovalSortKeysDto | null>[],
  t: TFunction
): NameDto<IBRebateApprovalSortKeysDto | null>[] => {
  const columnsOrder = [
    'tradingAccountName',
    'rebatePayoutStatusId',
    'accumulatedCommission',
    'rebateAmount',
  ]

  return columnsOrder
    .map((id) => {
      const option = sortOptions.find((x) => x.id === id)

      if (!option) return null

      if (id === 'rebatePayoutStatusId') {
        return {
          ...option,
          name: t('Status'),
        }
      }

      return option
    })
    .filter((option): option is NameDto<IBRebateApprovalSortKeysDto | null> => option !== null)
}
